import gsap from 'gsap';

/**
 * Utility class for managing GSAP animations with proper cleanup.
 * Prevents memory leaks by tracking and cleaning up animations.
 */
class GSAPManager {
    constructor() {
        this.animations = new Set();
        this.timelines = new Set();
        this.tweens = new Map();
    }

    /**
     * Create and track a GSAP timeline.
     * @param {Object} config - Timeline configuration
     * @returns {gsap.core.Timeline}
     */
    createTimeline(config = {}) {
        const timeline = gsap.timeline(config);
        this.timelines.add(timeline);
        return timeline;
    }

    /**
     * Create and track a GSAP tween.
     * @param {Object|Array} targets - Animation targets
     * @param {Object} vars - Animation variables
     * @param {string} [id] - Optional ID for the tween
     * @returns {gsap.core.Tween}
     */
    to(targets, vars, id = null) {
        const tween = gsap.to(targets, vars);
        
        if (id) {
            this.tweens.set(id, tween);
        }
        this.animations.add(tween);
        
        return tween;
    }

    /**
     * Create and track a GSAP from tween.
     * @param {Object|Array} targets - Animation targets
     * @param {Object} fromVars - From animation variables
     * @param {Object} toVars - To animation variables
     * @param {string} [id] - Optional ID for the tween
     * @returns {gsap.core.Tween}
     */
    fromTo(targets, fromVars, toVars, id = null) {
        const tween = gsap.fromTo(targets, fromVars, toVars);
        
        if (id) {
            this.tweens.set(id, tween);
        }
        this.animations.add(tween);
        
        return tween;
    }

    /**
     * Kill a specific animation by ID.
     * @param {string} id - Animation ID
     * @returns {boolean} - True if animation was found and killed
     */
    killAnimation(id) {
        const tween = this.tweens.get(id);
        if (tween) {
            tween.kill();
            this.tweens.delete(id);
            this.animations.delete(tween);
            return true;
        }
        return false;
    }

    /**
     * Kill all animations affecting specific targets.
     * @param {Object|Array} targets - Targets to kill animations for
     */
    killTweensOf(targets) {
        gsap.killTweensOf(targets);
        
        // Remove from our tracking
        this.animations.forEach((animation) => {
            if (animation.targets && this.targetsMatch(animation.targets(), targets)) {
                this.animations.delete(animation);
            }
        });
    }

    /**
     * Kill all tracked animations.
     */
    killAll() {
        // Kill all timelines
        this.timelines.forEach((timeline) => {
            timeline.kill();
        });
        this.timelines.clear();

        // Kill all tweens
        this.animations.forEach((animation) => {
            animation.kill();
        });
        this.animations.clear();

        // Clear tween map
        this.tweens.clear();
    }

    /**
     * Get a tween by ID.
     * @param {string} id - Animation ID
     * @returns {gsap.core.Tween|null}
     */
    getTween(id) {
        return this.tweens.get(id) || null;
    }

    /**
     * Check if animation with ID exists and is active.
     * @param {string} id - Animation ID
     * @returns {boolean}
     */
    isActive(id) {
        const tween = this.tweens.get(id);
        return tween && !tween.isActive() ? false : !!tween;
    }

    /**
     * Pause all animations.
     */
    pauseAll() {
        this.timelines.forEach((timeline) => timeline.pause());
        this.animations.forEach((animation) => animation.pause());
    }

    /**
     * Resume all animations.
     */
    resumeAll() {
        this.timelines.forEach((timeline) => timeline.resume());
        this.animations.forEach((animation) => animation.resume());
    }

    /**
     * Get statistics about tracked animations.
     * @returns {Object}
     */
    getStats() {
        return {
            totalAnimations: this.animations.size,
            totalTimelines: this.timelines.size,
            trackedTweens: this.tweens.size,
            tweenIds: Array.from(this.tweens.keys())
        };
    }

    /**
     * Check if targets match (simplified comparison).
     * @param {*} targets1 
     * @param {*} targets2 
     * @returns {boolean}
     */
    targetsMatch(targets1, targets2) {
        // Simple implementation - can be enhanced
        return targets1 === targets2;
    }

    /**
     * Clean up and prepare for garbage collection.
     */
    destroy() {
        this.killAll();
    }
}

// Create a singleton instance for global use
const gsapManager = new GSAPManager();

export default gsapManager;
export { GSAPManager };
